/**
 * Refresh-from-Open-Dental: diffing + result wording.
 *
 * This is a util rather than inline store/component code because two surfaces
 * have to agree — the store computes what changed, RefreshPlanButton says it
 * out loud, and the left pane badges the rows.
 *
 * Why it exists at all: a refresh that WORKED is otherwise indistinguishable
 * from one that did nothing. A procNum absent from `proceduresOrder` sorts to
 * the bottom (MAX_SAFE_INTEGER in `builderVisibleProcedures`), so on a 37-row
 * plan two newly-synced procedures land off-screen. The TC clicks, the spinner
 * turns, and nothing they can see changes.
 *
 * ProcNums are compared by identity, not coerced. Both sides of every diff come
 * from the same endpoint (`/get-treatment-plan-data` → `pl.ProcNum`), so the
 * types are consistent by construction; coercing would only mask a real change
 * in the payload shape.
 */

function toProcNums(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((p) => (p && typeof p === 'object' ? p.procNum : p))
    .filter((n) => n !== null && n !== undefined);
}

/**
 * Diff two procedure lists by ProcNum. Either side may be an array of
 * procedure objects or an array of raw procNums.
 *
 * @returns {{ added: Array, removed: Array }}
 */
export function diffProcedureSets(before, after) {
  const beforeNums = toProcNums(before);
  const afterNums = toProcNums(after);
  const beforeSet = new Set(beforeNums);
  const afterSet = new Set(afterNums);

  return {
    added: afterNums.filter((n) => !beforeSet.has(n)),
    removed: beforeNums.filter((n) => !afterSet.has(n)),
  };
}

/**
 * Human wording for the refresh pill's transient result state.
 *
 * "Up to date" is deliberately NOT phrased as "no changes in Open Dental" —
 * the mirror DB is eventually consistent (exe binlog → SQS → qprocessor), so a
 * click can legitimately land before a just-added procedure has replicated.
 * All we can honestly claim is that we checked.
 */
export function describeRefreshOutcome(outcome) {
  if (!outcome) return '';
  const added = toProcNums(outcome.added).length;
  const removed = toProcNums(outcome.removed).length;

  if (!added && !removed) return 'Up to date';
  if (added && removed) return `${added} added · ${removed} removed`;
  if (added) return `${added} ${added === 1 ? 'procedure' : 'procedures'} added`;
  return `${removed} ${removed === 1 ? 'procedure' : 'procedures'} removed`;
}

/**
 * Carry "new" marks across refreshes.
 *
 * Marks ACCUMULATE rather than replace, so a TC who refreshes twice in quick
 * succession doesn't lose the first batch of badges before they've looked at
 * them. Anything no longer present in the live procedure list is pruned — a
 * procedure added and then deleted again in OD shouldn't stay badged forever.
 */
export function mergeNewProcNums(existing, added, currentProcedures) {
  const live = new Set(toProcNums(currentProcedures));
  const seen = new Set();
  const merged = [];

  toProcNums(existing)
    .concat(toProcNums(added))
    .forEach((procNum) => {
      if (live.has(procNum) && !seen.has(procNum)) {
        seen.add(procNum);
        merged.push(procNum);
      }
    });

  return merged;
}
